/* ==========================================================================
   LSC Hero Slideshow - behaviour
   LSC - Created Oct 11, 2026 for sections/lsc-hero-slideshow.liquid.

   Defines <lsc-hero-slideshow>. The markup works without this file (the track
   is a native horizontal scroll-snap strip you can swipe); this adds:

     1. Active-slide tracking from the scroll position (works for swipes,
        trackpads, dots, arrows and keyboard alike).
     2. Dots + arrows + Left/Right arrow keys.
     3. Mouse drag-to-swipe on desktop (touch already swipes natively).
     4. Autoplay with a pause/play button. Autoplay also pauses while the
        pointer is over the slideshow, while focus is inside it, while it is
        off screen and while the tab is hidden. Visitors who ask for reduced
        motion start paused.
        LSC - Oct 11, 2026: the pause button also pauses the active slide's
        video. The pause holds (no auto-rotation, video stopped) until the
        visitor presses play OR moves to another slide (dot, arrow, swipe,
        drag or arrow key), which resumes both.
     5. Background videos: only the visible slot (desktop or mobile) of the
        ACTIVE slide gets a src and plays; others are paused. A smaller MP4
        rendition is looked up from the HLS playlist when the section asked
        for a quality cap below the largest file (same method as
        snippets/lsc-video.liquid).
     6. Inactive slides are made `inert`, so keyboard and screen-reader users
        only reach the visible slide's links.
     7. Theme editor support: selecting a slide block shows that slide and
        pauses autoplay; re-rendered sections re-initialise automatically
        (custom element connected/disconnected callbacks).
     8. "Shrink to fit media" (LSC - Oct 11, 2026): when the active slide has
        the option on, the track height animates to the media's own
        proportions; other slides animate back to the normal height.
     9. Per-slide settings (LSC - Oct 11, 2026): each slide has its own time
        on screen (data-lsc-duration, seconds) and its own dot colours
        (data-lsc-dot-color / data-lsc-dot-color-inactive; always rendered,
        default white). Not named data-lsc-dot: that selector finds the dot
        buttons.
    10. Audit fixes (LSC - Oct 11, 2026, see the project doc
        claude/lsc-hero-slideshow-audit.md):
          P2  video quality caps compare the rendition's height ("720p");
          P4  a tab that STARTS hidden doesn't rotate or fetch video;
          P6  the next slide's video starts downloading ~3 s before its turn;
          A1  pause/play names come from the theme's translations.
    11. LSC - Oct 11, 2026: videos restart from the first frame whenever
        their slide comes on screen (setActive / rewindVideos), and
        next/previous past either end of the strip moves forward/backward
        one slide instead of scrolling back past every slide (rotateFor).
    12. LSC - Oct 11, 2026 (speed): when this hero is the first section, the
        section starts the first slide's video from an inline script before
        this deferred file runs; loadVideo/playVideo pick that video up
        (video._lscLoad, is-playing) without re-fetching it.
    13. LSC - 2026-10-14: exact slide timing and video playback speed.
        - "Time on screen" runs from the moment a slide starts moving ON
          screen until it has fully moved OFF (both moves included). Each
          move is a fixed TRANSITION_MS animation (animateScroll) that starts
          that long before the time is up. The arriving slide starts as soon
          as the move begins: its video plays from the first frame while it
          slides in, and its own time starts counting (later Oct 14 change;
          first version started it on full arrival). The leaving slide's
          video keeps playing until it is off screen.
        - Video slides count their time by the video itself (schedule): the
          countdown starts when the video actually plays, follows its
          playback position after pauses, and the slide leaves at the point
          in the clip that makes the move end exactly at the time on screen,
          so a clip whose length matches the time on screen never visibly
          restarts while leaving. Image slides count wall-clock time, and a
          pause now resumes the countdown where it left off (audit P10).
        - Each video slide can set a playback speed (data-lsc-speed).

   No dependencies. Guarded so the file can safely load more than once (two
   copies of the section on one page each include the script tag).
   ========================================================================== */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('lsc-hero-slideshow')) return;

  /* ------------------------------------------------------------------------
     Shared helpers
     ------------------------------------------------------------------------ */

  // Viewer preference. Read live, because it can change while the page is open.
  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function prefersReducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  // Desktop/mobile split used by the CSS (768px), for re-checking which video
  // slot is visible when the window is resized across it.
  var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 768px)') : null;

  // 10. How many seconds before auto-rotate moves on the next slide's video
  // starts downloading (LSC - audit fix P6). ~3 s buffers a 2-3 MB file on
  // a typical connection without fetching it long before it is needed.
  var WARM_SECONDS = 3;

  // 13. LSC - 2026-10-14: length of the move from one slide to the next, in
  // ms. The move is animated here (animateScroll) rather than left to the
  // browser's smooth scrolling, whose length varies by browser and
  // distance, because the countdown needs to know it exactly: a slide starts
  // leaving TRANSITION_MS before its "Time on screen" is up, so the next
  // slide has fully arrived (and starts its own time) exactly at the end.
  var TRANSITION_MS = 700;

  // Ease-in-out (cubic): starts and ends gently, like a native smooth scroll.
  function easeInOut(p) {
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
  }

  // Listen to a MediaQueryList in old and new browsers.
  function onMediaChange(query, handler) {
    if (!query) return;
    if (query.addEventListener) query.addEventListener('change', handler);
    else if (query.addListener) query.addListener(handler);
  }

  /* ------------------------------------------------------------------------
     Video rendition resolver (shared by every slideshow on the page)

     Liquid's video.sources only exposes the HLS playlist and the LARGEST
     mp4. When that mp4 is above the chosen cap, the snippet adds
     data-lsc-hls (playlist URL) and data-lsc-max (cap). The playlist
     (~600 bytes) lists every rendition with its resolution, and each
     playlist variant name maps 1:1 to an mp4 file next to it on the CDN:
       {id}.HD-720p.2.1Mbps.hls.95567414_.m3u8
         -> {id}.HD-720p-2.1Mbps-95567414.mp4
     Any failure keeps the original mp4.
     ------------------------------------------------------------------------ */

  var playlistRequests = {}; // one request per playlist URL

  // Captures: width, height, id, HD|SD, lines, bitrate, version.
  var VARIANT = /RESOLUTION=(\d+)x(\d+)[^\n]*\n(\S+?)\.(HD|SD)-(\d+)p\.([\d.]+)Mbps\.hls\.(\d+)_\.m3u8/g;

  // Largest rendition whose HEIGHT fits the cap, as an mp4 file name.
  // LSC - Oct 11, 2026 (audit fix P2): compares height, the number in
  // Shopify's "720p" label, for portrait and landscape alike. It used to
  // compare the short side, which let a vertical 606x1080 file pass a 720
  // cap (see the header of snippets/lsc-hero-media.liquid).
  function pickRendition(playlist, maxHeight) {
    var best = null;
    var match;
    VARIANT.lastIndex = 0;
    while ((match = VARIANT.exec(playlist))) {
      var height = Number(match[2]);
      if (height <= maxHeight && (!best || height > best.height)) {
        best = {
          height: height,
          file: match[3] + '.' + match[4] + '-' + match[5] + 'p-' + match[6] + 'Mbps-' + match[7] + '.mp4'
        };
      }
    }
    return best;
  }

  // Resolves to the URL this <video> should play.
  function resolveVideoSrc(video) {
    var fallback = video.getAttribute('data-lsc-src');
    var playlistUrl = video.getAttribute('data-lsc-hls');
    if (!playlistUrl || !window.fetch) return Promise.resolve(fallback);

    var maxHeight = Number(video.getAttribute('data-lsc-max')) || 720;
    if (!playlistRequests[playlistUrl]) {
      playlistRequests[playlistUrl] = fetch(playlistUrl).then(function (response) {
        if (!response.ok) throw new Error('playlist ' + response.status);
        return response.text();
      });
    }

    return playlistRequests[playlistUrl]
      .then(function (playlist) {
        var rendition = pickRendition(playlist, maxHeight);
        if (!rendition) return fallback;
        video.setAttribute('data-lsc-rendition', rendition.height + 'p');
        return playlistUrl.slice(0, playlistUrl.lastIndexOf('/') + 1) + rendition.file;
      })
      .catch(function () {
        return fallback;
      });
  }

  /* ------------------------------------------------------------------------
     The element
     ------------------------------------------------------------------------ */

  class LscHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lsc-track]');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-lsc-slide]'));
      // Dot buttons only (button[...]), so no other element carrying a
      // similarly named attribute can ever be mistaken for a dot.
      this.dots = Array.prototype.slice.call(this.querySelectorAll('button[data-lsc-dot]'));
      this.pauseButton = this.querySelector('[data-lsc-pause]');
      this.prevButton = this.querySelector('[data-lsc-prev]');
      this.nextButton = this.querySelector('[data-lsc-next]');

      // LSC - Oct 11, 2026: dot N controls whatever slide is Nth now. When the
      // section's "Random" slide order has shuffled the slides (inline script
      // in the section), the Liquid-written aria-controls point at the old
      // order, so re-point them here. Harmless when nothing was shuffled.
      var slides = this.slides;
      this.dots.forEach(function (dot, d) {
        if (slides[d] && slides[d].id) dot.setAttribute('aria-controls', slides[d].id);
      });

      this.index = 0;
      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true' && this.slides.length > 1;
      // 9. Time per slide is set on each slide (data-lsc-duration); this is
      // only the fallback for a slide without one.
      this.defaultSeconds = 10;

      // Autoplay runs only when NONE of these "pause reasons" is set.
      //   user:    pressed pause; also stops the active video. Cleared by
      //            play or by moving to another slide.
      //   motion:  visitor prefers reduced motion; set at load, also keeps
      //            videos on their poster. Cleared only by pressing play.
      //   hover:   pointer is over the slideshow
      //   focus:   keyboard focus is inside it
      //   offscreen / hidden: not visible / tab in the background
      //   editor:  a block is selected in the theme editor
      this.pauseReasons = {};
      if (prefersReducedMotion()) this.pauseReasons.motion = true;
      // LSC - Oct 11, 2026 (audit fix P4): a page opened in a background tab
      // (e.g. middle-click from an email) starts hidden and never fires
      // visibilitychange until it is shown. Without this it rotated slides
      // and downloaded a 3 MB video nobody saw.
      if (document.hidden) this.pauseReasons.hidden = true;

      this.timer = null;
      this.warmTimer = null; // 10. see warmNext()
      this.cleanups = [];

      // 13. Countdown bookkeeping for the active slide (see schedule):
      //   elapsed:  seconds of wall-clock countdown already used up;
      //   runStart: when the current wall-clock run started (null if none).
      // And the slide-move animation state (see animateScroll).
      this.elapsed = 0;
      this.runStart = null;
      this.animating = false;
      this.rafId = 0;
      this.animFallback = null;

      // 8. "Shrink to fit media". Only when the section rendered the
      // has-fit class (some slide uses it); otherwise heights are pure CSS.
      this.fitEnabled = this.classList.contains('lsc-hero--has-fit');
      this.sizer = this.querySelector('.lsc-hero__sizer');
      this.lastHeight = null;

      this.bindEvents();
      this.setActive(this.currentIndexFromScroll(), true);
      this.updatePauseButton();
      this.schedule();

      // Things that change how much room the text needs after first layout.
      if (this.fitEnabled) this.watchLayout();
    }

    // 8. Re-check the height (instantly, so it never looks like a resize)
    // whenever the text's space needs change after the first layout:
    //   - web fonts finish loading, or text re-wraps (ResizeObserver on each
    //     slide's content box; the box's height doesn't depend on the track
    //     height, so animating the track never re-triggers this);
    //   - the theme writes --header-height onto <html style="..."> one frame
    //     after load, which adds to the text's top padding under a transparent
    //     header (found Oct 11: measured 411px before, 470px after on a phone);
    //   - window load, as a final catch-all.
    // Batched to one check per animation frame.
    watchLayout() {
      var self = this;
      var queued = false;
      var refresh = function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          if (self.isConnected) self.applyHeight(true);
        });
      };

      if ('ResizeObserver' in window) {
        var resizeObserver = new ResizeObserver(refresh);
        Array.prototype.forEach.call(this.querySelectorAll('.lsc-hero__content'), function (box) {
          resizeObserver.observe(box);
        });
        this.cleanups.push(function () { resizeObserver.disconnect(); });
      }

      if ('MutationObserver' in window) {
        var styleObserver = new MutationObserver(refresh);
        styleObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
        this.cleanups.push(function () { styleObserver.disconnect(); });
      }

      if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
      this.listen(window, 'load', refresh);
    }

    disconnectedCallback() {
      // Theme editor re-renders sections: remove every listener and timer.
      this.stopAnimation();
      this.clearTimer();
      this.cleanups.forEach(function (fn) { fn(); });
      this.cleanups = [];
      this.pauseAllVideos();
    }

    // addEventListener that is automatically removed on disconnect.
    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(function () { target.removeEventListener(type, handler, options); });
    }

    /* ---------------- Events ---------------- */

    bindEvents() {
      var self = this;

      // 1. Scroll -> active slide. rAF-throttled.
      // A new slide arriving while the visitor's pause is on can only come
      // from the visitor (auto-rotation is stopped), e.g. a native swipe, so
      // it ends the pause before the new slide's video is started.
      var ticking = false;
      this.listen(this.track, 'scroll', function () {
        // 13. Our own slide-move animation sets the scroll position every
        // frame; the slide change is applied when it ends, not on the way.
        if (self.animating) return;
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var i = self.currentIndexFromScroll();
          if (i !== self.index) {
            self.userNavigated();
            self.setActive(i);
            self.schedule(); // a manual swipe restarts the timer
          }
        });
      }, { passive: true });

      // 13. A touch or wheel during a slide move hands control back to the
      // visitor: the animation stops where it is, native scroll-snap takes
      // over, and the scroll listener above picks up whichever slide they
      // settle on. The current slide's countdown restarts (they interacted).
      // (The arriving slide is already the active one: goTo switches at the
      // start of the move. If they settle back on the old slide, the scroll
      // listener switches back, height included.)
      var interrupt = function () {
        if (!self.animating) return;
        self.stopAnimation();
        self.elapsed = 0;
        self.runStart = null;
        self.schedule();
      };
      this.listen(this.track, 'touchstart', interrupt, { passive: true });
      this.listen(this.track, 'wheel', interrupt, { passive: true });

      // 2. Dots, arrows.
      // Each ends a visitor pause (see userNavigated) before moving.
      this.dots.forEach(function (dot) {
        self.listen(dot, 'click', function () {
          self.userNavigated();
          self.goTo(Number(dot.getAttribute('data-lsc-dot')));
        });
      });
      if (this.prevButton) this.listen(this.prevButton, 'click', function () { self.userNavigated(); self.goTo(self.index - 1); });
      if (this.nextButton) this.listen(this.nextButton, 'click', function () { self.userNavigated(); self.goTo(self.index + 1); });

      // 2b. Keyboard: Left/Right when focus is in the slideshow (but not in a text field).
      this.listen(this, 'keydown', function (event) {
        if (event.target && /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); self.userNavigated(); self.goTo(self.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); self.userNavigated(); self.goTo(self.index + 1); }
      });

      // 4. Pause/play button. Pause stops auto-rotation AND the active
      // video; play restarts both. Play also lifts the reduced-motion start
      // state, since pressing it is an explicit request for motion.
      if (this.pauseButton) {
        this.listen(this.pauseButton, 'click', function () {
          if (self.pauseReasons.user || self.pauseReasons.motion) {
            delete self.pauseReasons.user;
            delete self.pauseReasons.motion;
          } else {
            self.pauseReasons.user = true;
          }
          self.updatePauseButton();
          self.schedule();
          self.syncVideos(); // pause or resume the visible video right away
        });
      }

      // 4. Hover / focus pauses.
      // Hover: real mouse only. A tap on a phone fires emulated mouse events
      // that would otherwise leave autoplay "hovered" (paused) indefinitely.
      this.listen(this, 'pointerenter', function (event) {
        if (event.pointerType === 'mouse') self.setPause('hover', true);
      });
      this.listen(this, 'pointerleave', function (event) {
        if (event.pointerType === 'mouse') self.setPause('hover', false);
      });
      // Focus: keyboard focus only (:focus-visible). A mouse click or tap on a
      // dot also focuses it, and that shouldn't stop the rotation.
      this.listen(this, 'focusin', function (event) {
        var keyboard = true;
        try { keyboard = event.target.matches(':focus-visible'); } catch (e) { /* old browser: assume keyboard */ }
        if (keyboard) self.setPause('focus', true);
      });
      this.listen(this, 'focusout', function (event) {
        if (!self.contains(event.relatedTarget)) self.setPause('focus', false);
      });

      // 4. Off screen: pause autoplay AND the active video (saves battery/data).
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          var visible = entries[0] && entries[0].isIntersecting;
          self.setPause('offscreen', !visible);
          if (visible) self.syncVideos();
          else self.pauseAllVideos();
        }, { threshold: 0.15 });
        observer.observe(this);
        this.cleanups.push(function () { observer.disconnect(); });
      }

      // 4. Background tab. LSC - P4: also stops the video while hidden and
      // starts it (fetching its file for the first time if needed) once the
      // tab is shown.
      this.listen(document, 'visibilitychange', function () {
        self.setPause('hidden', document.hidden);
        if (document.hidden) self.pauseAllVideos();
        else self.syncVideos();
      });

      // 5. Crossing the 768px split swaps which video slot is visible.
      if (desktopQuery) {
        var onBreakpoint = function () {
          self.pauseAllVideos();
          self.syncVideos();
          self.applyHeight(true); // mobile and desktop media can differ in shape
        };
        onMediaChange(desktopQuery, onBreakpoint);
        this.cleanups.push(function () {
          if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onBreakpoint);
          else if (desktopQuery.removeListener) desktopQuery.removeListener(onBreakpoint);
        });
      }

      // Window resize: keep the current slide snapped (slide width changes).
      var resizeTimer = null;
      this.listen(window, 'resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          // Height first (instant): width changed, so fitted heights did too.
          // The re-snap below then finds the height already right.
          self.applyHeight(true);
          self.goTo(self.index, true);
        }, 150);
      });

      // 3. Mouse drag-to-swipe (touch and pen already scroll natively).
      this.bindMouseDrag();

      // 7. Theme editor.
      this.listen(this, 'shopify:block:select', function (event) {
        var slide = event.target && event.target.closest ? event.target.closest('[data-lsc-slide]') : null;
        if (!slide) return;
        self.setPause('editor', true);
        self.goTo(Number(slide.getAttribute('data-lsc-slide')), true);
      });
      this.listen(this, 'shopify:block:deselect', function () {
        self.setPause('editor', false);
      });
    }

    // Desktop mouse drag. Moves the strip with the pointer, then snaps to the
    // nearest slide (or the next/previous one after a 50px fling).
    bindMouseDrag() {
      var self = this;
      var track = this.track;
      var startX = 0;
      var startScroll = 0;
      var dragging = false;
      var moved = false;

      this.listen(track, 'pointerdown', function (event) {
        // Reset first, so a drag that ended outside the window can never
        // swallow the next real click (see the click guard below).
        moved = false;
        if (event.pointerType !== 'mouse' || event.button !== 0 || self.slides.length < 2) return;
        // Let buttons and links inside the content take normal clicks.
        if (event.target.closest('.lsc-hero__content')) return;
        // 13. Grabbing the strip mid-move stops the move where it is; the
        // countdown restarts (a drag then moves on via pointerup -> goTo).
        if (self.animating) {
          self.stopAnimation();
          self.elapsed = 0;
          self.runStart = null;
          self.schedule();
        }
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      });

      this.listen(window, 'pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startScroll - dx;
      });

      this.listen(window, 'pointerup', function (event) {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        var dx = event.clientX - startX;
        track.classList.remove('is-dragging');
        self.userNavigated();
        // LSC - Oct 11, 2026: work in on-screen positions (the strip may be
        // rotated by the seamless wrap, see rotateFor), then convert the
        // slide landed on to its slide number.
        var pos = self.indexFromScroll(startScroll);
        if (dx < -50) pos += 1;
        else if (dx > 50) pos -= 1;
        pos = Math.max(0, Math.min(self.slides.length - 1, pos));
        var landed = self.slides.indexOf(track.children[pos]);
        self.goTo(landed === -1 ? self.index : landed);
      });

      // A drag must not also "click" the whole-slide link underneath.
      this.listen(track, 'click', function (event) {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      }, true);

      // Stop the browser's native image drag ghost.
      this.listen(track, 'dragstart', function (event) { event.preventDefault(); });
    }

    /* ---------------- Position ---------------- */

    // ON-SCREEN position (0 = leftmost) for a given scrollLeft (Math.abs
    // handles RTL, where scrollLeft is negative).
    // LSC - Oct 11, 2026: on-screen position and slide number can differ
    // once the seamless wrap has rotated the strip (see rotateFor). Slide
    // numbers (this.index, this.slides[i], dots) never change; only where
    // each slide sits in the strip does.
    indexFromScroll(scrollLeft) {
      var width = this.track.clientWidth || 1;
      var i = Math.round(Math.abs(scrollLeft) / width);
      return Math.max(0, Math.min(this.slides.length - 1, i));
    }

    // Slide NUMBER of the slide currently on screen.
    currentIndexFromScroll() {
      var slide = this.track.children[this.indexFromScroll(this.track.scrollLeft)];
      var i = this.slides.indexOf(slide);
      return i === -1 ? this.index : i;
    }

    // scrollLeft that puts `slide` on screen, wherever it sits in the strip.
    scrollLeftFor(slide) {
      var left = slide.offsetLeft - this.track.offsetLeft;
      if (document.dir === 'rtl' || getComputedStyle(this.track).direction === 'rtl') {
        left = -(this.track.scrollWidth - this.track.clientWidth - left);
      }
      return left;
    }

    // LSC - Oct 11, 2026: seamless wrap-around.
    // Before, going from the last slide to the first scrolled BACKWARDS past
    // every slide in between (e.g. 3-2-1, then 2 flashing by on the way back
    // to 3), which looked like the order was being reshuffled. Now, for a
    // one-step move past either end, the slide at the far end is moved in
    // the page to sit next to the current one, the scroll position is
    // corrected instantly so nothing visibly moves, and the normal smooth
    // scroll then goes one slide in the expected direction.
    // The strip stays a rotation of the slide order, so the order shown
    // never changes; only the starting point of the strip does.
    //   step: +1 (next) or -1 (previous)
    rotateFor(step) {
      var track = this.track;
      var count = track.children.length;
      if (count < 3) return; // with 2 slides the other one is always adjacent
      var current = this.slides[this.index];
      var pos = Array.prototype.indexOf.call(track.children, current);
      if (step > 0 && pos === count - 1) {
        track.appendChild(track.firstElementChild);                   // first -> end
      } else if (step < 0 && pos === 0) {
        track.insertBefore(track.lastElementChild, track.firstElementChild); // last -> front
      } else {
        return; // the neighbour is already beside it
      }
      // Keep the current slide exactly where it was on screen.
      track.scrollLeft = this.scrollLeftFor(current);
    }

    // Scroll to slide i (wraps around at both ends). `instant` skips the
    // animation (used for resize and the theme editor).
    // LSC - 2026-10-14: the move is our own fixed-length animation
    // (animateScroll).
    // LSC - 2026-10-14 (later, per request): the slide change (dots, video
    // switch, countdown) is applied the moment the new slide STARTS moving
    // on screen, not when it has fully arrived: its video starts from the
    // first frame as it slides in, and its time on screen starts counting
    // then. The leaving slide's video keeps playing until the move ends
    // (this.leavingVideo is exempt from syncVideos' pause), then pauses.
    goTo(i, instant) {
      var self = this;
      var count = this.slides.length;
      if (!count) return;
      var target = ((i % count) + count) % count;
      this.stopAnimation();
      // A one-step move (next/previous, incl. auto-rotate) that crosses an
      // end of the strip: bring the neighbour alongside first.
      var step = i - this.index;
      if (!instant && (step === 1 || step === -1) && target !== this.index) {
        this.rotateFor(step);
      }
      var left = this.scrollLeftFor(this.slides[target]);

      if (instant || prefersReducedMotion()) {
        this.track.scrollLeft = left;
        this.setActive(target);
        this.schedule();
        return;
      }

      // The leaving slide's video keeps playing during the move but must not
      // visibly loop back to its first frame if the move runs a few ms past
      // the clip's end (busy main thread): loop is switched off for the move,
      // so it holds its last frame instead. stopAnimation restores it and
      // pauses it (it is no longer the active slide's video).
      var leaving = target !== this.index ? this.activeVideo() : null;
      if (leaving) {
        this.leavingVideo = leaving;
        this.leavingLoop = leaving.loop;
        leaving.loop = false;
      }
      // Start the arriving slide NOW, as it begins moving on screen:
      // setActive rewinds and plays its video (and, with "Shrink to fit",
      // animates to its height alongside the move, both 0.7 s), and
      // schedule starts its time on screen from this moment.
      this.setActive(target);
      this.schedule();
      this.animateScroll(left, function () {
        // On arrival: stopAnimation (already called by animateScroll's
        // finish) pauses the leaving video. Called again here for the
        // no-distance case, where animateScroll calls done() directly;
        // a second call does nothing.
        self.stopAnimation();
      });
    }

    // 13. Animate the strip's scroll position to `left` over TRANSITION_MS,
    // then call done(). Scroll-snap is switched off while it runs (CSS
    // .is-animating), otherwise the browser would snap every frame. A
    // backup timer finishes the move if animation frames are not delivered
    // (e.g. the tab was hidden mid-move).
    animateScroll(left, done) {
      var self = this;
      var track = this.track;
      var from = track.scrollLeft;
      var distance = left - from;
      if (Math.abs(distance) < 1) {
        track.scrollLeft = left;
        done();
        return;
      }
      // Timed from this call (not the first frame, which comes up to a frame
      // later), so the move ends TRANSITION_MS after the countdown fired.
      // rAF timestamps share performance.now()'s clock.
      var startedAt = window.performance && performance.now ? performance.now() : null;
      var finish = function () {
        if (!self.animating) return;
        track.scrollLeft = left;   // land exactly on the snap point...
        self.stopAnimation();      // ...then let scroll-snap back on
        done();
      };
      var frame = function (now) {
        if (!self.animating) return;
        if (startedAt === null) startedAt = now;
        var p = Math.max(0, Math.min(1, (now - startedAt) / TRANSITION_MS));
        track.scrollLeft = from + distance * easeInOut(p);
        if (p < 1) self.rafId = window.requestAnimationFrame(frame);
        else finish();
      };
      this.animating = true;
      track.classList.add('is-animating');
      this.rafId = window.requestAnimationFrame(frame);
      this.animFallback = setTimeout(finish, TRANSITION_MS + 300);
    }

    // Stop a running slide move where it is (no done() call).
    stopAnimation() {
      // The move is over (or cut short): give the leaving video (see goTo)
      // its loop back and pause it, unless it is the active slide's video
      // again (e.g. a dot click straight back to that slide).
      if (this.leavingVideo) {
        var leaving = this.leavingVideo;
        this.leavingVideo = null;
        leaving.loop = this.leavingLoop !== false;
        if (!this.shouldPlay(leaving) && !leaving.paused) leaving.pause();
      }
      if (!this.animating) return;
      this.animating = false;
      window.cancelAnimationFrame(this.rafId);
      clearTimeout(this.animFallback);
      this.animFallback = null;
      this.track.classList.remove('is-animating');
    }

    // The visitor moved to a slide themselves: end their pause (if any), so
    // auto-rotation resumes and the slide they land on plays its video.
    // The reduced-motion state is left alone; only the play button lifts it.
    userNavigated() {
      if (!this.pauseReasons.user) return;
      delete this.pauseReasons.user;
      this.updatePauseButton();
      this.schedule();
    }

    /* ---------------- Active slide ---------------- */

    setActive(i, initial) {
      var changed = i !== this.index;
      this.index = i;

      // LSC - Oct 11, 2026: a slide's video always starts from the
      // beginning when its slide comes on screen. Videos loop, and a slide's
      // time on screen rarely matches the video's length, so without this a
      // returning slide resumed mid-clip (out of sync with its text/timing).
      // Only on an actual slide change: pausing (button, off screen, hidden
      // tab) and resuming on the same slide still continues where it was.
      if (changed) this.rewindVideos(this.slides[i]);

      // 13. A new slide starts its time on screen from zero.
      if (changed) {
        this.elapsed = 0;
        this.runStart = null;
      }

      // Dots.
      this.dots.forEach(function (dot, d) {
        if (d === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      // Slides: only the active one is reachable by keyboard / screen reader.
      // `inert` does not block scrolling, so swiping still works.
      this.slides.forEach(function (slide, s) {
        if (s === i) {
          slide.removeAttribute('inert');
          slide.removeAttribute('aria-hidden');
          slide.classList.add('is-active');
        } else {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
          slide.classList.remove('is-active');
        }
      });

      this.syncVideos();

      // 9. This slide's dot colours, if it overrides the section's.
      this.applyDotColors();

      // 8. Animate to this slide's height (instant on first run).
      this.applyHeight(!!initial);
    }

    /* ---------------- 9. Per-slide dot colours ---------------- */

    // Set the dot colour variables on the slideshow element to the active
    // slide's colours (LSC - Oct 11, 2026: dot colours are per slide; the
    // section always renders both attributes). If one is ever missing, the
    // variable is cleared and the section's <style> value (the first
    // slide's colours) applies. The CSS transition fades the change.
    applyDotColors() {
      var slide = this.slides[this.index];
      if (!slide) return;
      var pairs = [
        ['--lsc-hero-dot', slide.getAttribute('data-lsc-dot-color')],
        ['--lsc-hero-dot-inactive', slide.getAttribute('data-lsc-dot-color-inactive')]
      ];
      for (var p = 0; p < pairs.length; p++) {
        if (pairs[p][1]) this.style.setProperty(pairs[p][0], pairs[p][1]);
        else this.style.removeProperty(pairs[p][0]);
      }
    }

    /* ---------------- 8. Shrink to fit media ---------------- */

    // Height the active slide's text needs: the content box plus the text
    // layer's top/bottom padding (which includes the header offset).
    contentNeed(slide) {
      var inner = slide.querySelector('.lsc-hero__inner');
      var content = inner && inner.querySelector('.lsc-hero__content');
      if (!content) return 0;
      var cs = window.getComputedStyle(inner);
      return content.offsetHeight + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    }

    // Target height for slide i, in px:
    //   normal = the CSS normal height (sizer), or more if the text needs it;
    //   fitted = width / media proportions, never below what the text needs.
    //            Desktop: never above normal (the option only shrinks).
    //            Phones (LSC - Oct 11, 2026 fix): NOT capped, so the whole
    //            media shows. A vertical 9:16 video at full phone width needs
    //            ~700px, but the normal height is 75% of the screen (~495px
    //            in iPhone Safari with its toolbars), so capping cut ~100px
    //            off the top and bottom, right under the header.
    targetHeight(i) {
      var slide = this.slides[i];
      if (!slide) return null;
      var need = this.contentNeed(slide);
      var normal = Math.max(this.sizer ? this.sizer.offsetHeight : 0, need);
      if (slide.getAttribute('data-lsc-fit') !== 'true') return Math.round(normal);

      var desktop = desktopQuery ? desktopQuery.matches : true;
      var ratio = Number(slide.getAttribute(desktop ? 'data-lsc-ar-d' : 'data-lsc-ar-m'));
      if (!ratio) return Math.round(normal);
      var fitted = this.track.clientWidth / ratio;
      if (!desktop) return Math.round(Math.max(fitted, need)); // phones: whole media, may extend below the fold
      return Math.round(Math.min(normal, Math.max(fitted, need)));
    }

    // Set the track height. `instant` skips the animation (first load,
    // resizes, font swaps, header height); otherwise the CSS transition
    // animates it. `i` (optional, LSC - 2026-10-14) sizes for slide i
    // instead of the active slide.
    applyHeight(instant, i) {
      if (!this.fitEnabled) return;
      var height = this.targetHeight(i === undefined ? this.index : i);
      if (height === null || height <= 0) return;
      // Unchanged: do nothing, so a re-check can never cut short an
      // animation that is already heading to this same height.
      if (height === this.lastHeight) return;
      this.lastHeight = height;

      var track = this.track;
      if (instant) {
        track.classList.add('is-instant');
        track.style.height = height + 'px';
        void track.offsetHeight; // commit the new height before re-enabling the transition
        window.requestAnimationFrame(function () { track.classList.remove('is-instant'); });
      } else {
        track.style.height = height + 'px';
      }
    }

    /* ---------------- Videos ---------------- */

    // Play the visible video(s) on the active slide, pause all the others.
    // Nothing plays while the visitor's pause is on, while the reduced-motion
    // start state is on (the poster stays), while the section is off screen,
    // or (LSC - P4) while the tab is hidden. A held video that never played
    // also never gets its file, so a hidden tab downloads no video at all.
    syncVideos() {
      var self = this;
      this.slides.forEach(function (slide) {
        var videos = slide.querySelectorAll('video[data-lsc-src]');
        Array.prototype.forEach.call(videos, function (video) {
          if (self.shouldPlay(video)) {
            self.playVideo(video);
          } else if (!video.paused && !(video === self.leavingVideo && !self.videosHeld())) {
            // LSC - 2026-10-14: except the video of the slide that is still
            // moving off screen (goTo / stopAnimation), unless video is held.
            video.pause();
          }
        });
      });
    }

    // True while something stops all video (see syncVideos).
    videosHeld() {
      var r = this.pauseReasons;
      return !!(r.user || r.motion || r.offscreen || r.hidden);
    }

    // Is this slot shown at the current breakpoint? (The other slot is
    // display:none, so it has no layout box.)
    slotVisible(video) {
      return video.offsetParent !== null || video.getClientRects().length > 0;
    }

    // Should this video be playing right now: on the active slide, in the
    // visible slot, and nothing holding video.
    shouldPlay(video) {
      var slide = video.closest('[data-lsc-slide]');
      return slide === this.slides[this.index] && this.slotVisible(video) && !this.videosHeld();
    }

    // Give a video its file (once) without playing it. Resolves true when
    // the video has a src. Shared by playVideo and warmNext, so a video the
    // warm-up already started is never fetched twice: the second caller just
    // waits for the same promise.
    // LSC - Oct 11, 2026 (speed): the first slide's video may already have a
    // _lscLoad promise from the section's inline early-start script; it is
    // reused here as-is (same contract: resolves true once src is set).
    loadVideo(video) {
      if (video.getAttribute('src')) return Promise.resolve(true);
      if (!video._lscLoad) {
        var self = this;
        video._lscLoad = resolveVideoSrc(video).then(function (src) {
          if (!src) return false;
          video.setAttribute('preload', 'auto'); // buffer now, not on first play
          video.src = src;
          self.applySpeed(video); // after src: a new src resets the rate
          return true;
        });
      }
      return video._lscLoad;
    }

    playVideo(video) {
      var self = this;
      var media = video.closest('.lsc-hero__media');
      // Required for autoplay policies (the attribute alone is not always enough).
      video.muted = true;
      video.playsInline = true;

      var start = function () {
        var attempt = video.play();
        if (attempt && attempt.catch) {
          attempt.catch(function () { /* blocked (e.g. Low Power Mode): poster stays */ });
        }
      };

      // First play: show it only once real frames are on screen, so the
      // poster never flashes to black.
      if (!video.hasAttribute('data-lsc-wired')) {
        video.setAttribute('data-lsc-wired', '');
        video.addEventListener('playing', function () {
          if (media) media.classList.add('is-playing');
          // 13. The video really started (or resumed): re-time the slide
          // from the video's own playback position.
          if (self.shouldPlay(video)) self.schedule();
        });
        // 13. A warmed-up (preloaded) video shows its own first frame as
        // soon as it has one, so the arriving slide shows exactly the frame
        // the clip starts on, rather than the poster.
        video.addEventListener('loadeddata', function () {
          if (media) media.classList.add('is-playing');
        });
      }
      this.applySpeed(video);
      // LSC - Oct 11, 2026 (speed): the section's inline early-start script
      // may have started this video before this file ran, in which case its
      // 'playing' event has already fired. Show it if frames are running.
      if (media && !video.paused && video.readyState >= 3) media.classList.add('is-playing');

      if (video.getAttribute('src')) {
        start();
        return;
      }
      // First play: fetch the file (or join a warm-up already fetching it),
      // then start only if this video should STILL be playing. The playlist
      // lookup takes a moment, and the visitor may have moved on meanwhile.
      this.loadVideo(video).then(function (ok) {
        if (ok && self.shouldPlay(video)) start();
      });
    }

    /* ---------------- 10. Warm up the next video ---------------- */

    // LSC - Oct 11, 2026 (audit fix P6): a few seconds before auto-rotate
    // moves on, give the NEXT slide's visible video its file, so it is
    // already buffering when the slide arrives instead of showing its
    // poster while the first bytes load. Auto-rotate always reaches that
    // slide, so this costs no extra data. Only called from the countdown
    // (schedule), so it never runs while paused, off screen or hidden.
    warmNext() {
      if (!this.canWarm()) return;
      var count = this.slides.length;
      if (count < 2) return;
      var next = this.slides[(this.index + 1) % count];
      var self = this;
      Array.prototype.forEach.call(next.querySelectorAll('video[data-lsc-src]'), function (video) {
        if (self.slotVisible(video)) self.loadVideo(video);
      });
    }

    // Skip the warm-up for visitors who asked to save data or are on a slow
    // connection (Network Information API; Chrome/Android only, others
    // simply warm). On those, an early 2-3 MB download would compete with
    // the rest of the page for little gain.
    canWarm() {
      var c = navigator.connection;
      if (!c) return true;
      if (c.saveData) return false;
      return !/(^|-)2g$|^3g$/.test(c.effectiveType || '');
    }

    // 13. Playback speed of the slide this video belongs to (data-lsc-speed,
    // 0.5-1.5; 1 when not set). defaultPlaybackRate too, so the rate
    // survives the browser resetting playbackRate when a src is loaded.
    applySpeed(video) {
      var slide = video.closest('[data-lsc-slide]');
      var speed = slide ? Number(slide.getAttribute('data-lsc-speed')) : 0;
      if (!speed) speed = 1;
      if (video.defaultPlaybackRate !== speed) video.defaultPlaybackRate = speed;
      if (video.playbackRate !== speed) video.playbackRate = speed;
    }

    // 13. The video shown on the active slide at this screen width, or null.
    activeVideo() {
      var slide = this.slides[this.index];
      if (!slide) return null;
      var videos = slide.querySelectorAll('video[data-lsc-src]');
      for (var v = 0; v < videos.length; v++) {
        if (this.slotVisible(videos[v])) return videos[v];
      }
      return null;
    }

    // Put every video on `slide` back to its first frame (see setActive).
    // A video that has no file yet simply starts at 0 when it gets one.
    rewindVideos(slide) {
      if (!slide) return;
      Array.prototype.forEach.call(slide.querySelectorAll('video[data-lsc-src]'), function (video) {
        try {
          if (video.currentTime > 0) video.currentTime = 0;
        } catch (e) { /* not seekable yet: it will start at 0 anyway */ }
      });
    }

    pauseAllVideos() {
      var videos = this.querySelectorAll('video[data-lsc-src]');
      Array.prototype.forEach.call(videos, function (video) {
        if (!video.paused) video.pause();
      });
    }

    /* ---------------- Autoplay ---------------- */

    setPause(reason, on) {
      if (on) this.pauseReasons[reason] = true;
      else delete this.pauseReasons[reason];
      this.schedule();
    }

    isPaused() {
      return Object.keys(this.pauseReasons).length > 0;
    }

    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      // 10. The warm-up belongs to the countdown it was set with.
      if (this.warmTimer) {
        clearTimeout(this.warmTimer);
        this.warmTimer = null;
      }
      // 13. Bank the wall-clock time this run used, so a pause resumes the
      // countdown instead of restarting it (audit P10).
      if (this.runStart !== null) {
        this.elapsed += (Date.now() - this.runStart) / 1000;
        this.runStart = null;
      }
    }

    // (Re)start the countdown to the next slide, or stop it if paused.
    // 9. The countdown is the ACTIVE slide's own time on screen.
    // LSC - 2026-10-14 (13): a slide's time on screen runs from the moment
    // it STARTS moving on screen (goTo calls setActive + schedule then; the
    // countdown may run during the move) until it has fully moved OFF. So
    // the countdown ends when the slide must start leaving: its time on
    // screen minus the length of the move. The next slide starts (video
    // from frame 0, its own time counting) at that same moment.
    //   - Video slide, video playing, and the time on screen fits within one
    //     play of the clip (time x speed <= clip length): timed from the
    //     video's playback position. The slide leaves when the clip reaches
    //     (time - move) x speed, so the clip is still playing, not
    //     restarting, while the slide moves off. After a pause (hover etc.)
    //     the clip may have looped; then it waits for the same point in the
    //     clip (or leaves at once if the slide is shorter than the clip).
    //   - Video not playing yet (still loading, or autoplay blocked), or a
    //     slide longer than its clip: wall-clock time, and the 'playing'
    //     event (see playVideo) re-times it once the video plays.
    //   - Image slide: wall-clock time, resumed after pauses.
    schedule() {
      var self = this;
      this.clearTimer();
      if (!this.autoplayEnabled) return;

      // Screen readers shouldn't announce every automatic change.
      this.track.setAttribute('aria-live', this.isPaused() ? 'polite' : 'off');

      // (Not blocked while a move is running: the arriving slide's time
      // starts at the beginning of the move, and its video's 'playing'
      // event re-times it mid-move.)
      if (this.isPaused()) return;

      var seconds = this.slideSeconds(this.index);
      // The move counts inside the time on screen (instant with reduced
      // motion; never more than half a short slide).
      var move = prefersReducedMotion() ? 0 : Math.min(TRANSITION_MS / 1000, seconds / 2);
      var wait = null;

      var video = this.activeVideo();
      if (video && !video.paused && video.readyState >= 2 && isFinite(video.duration) && video.duration > 0) {
        var rate = video.playbackRate || 1;
        var clip = video.duration;          // media seconds
        var media = seconds * rate;         // media seconds the slide spans
        if (media <= clip + 0.05) {
          var leaveAt = (seconds - move) * rate;  // clip position to start leaving at
          wait = (leaveAt - video.currentTime) / rate;
          if (wait < 0) {
            // Past that point in this play of the clip (it looped during a
            // pause). A slide as long as its clip waits for the same point
            // in the next play; a shorter slide simply leaves now.
            wait = media >= clip - 0.05 ? wait + clip / rate : 0;
          }
        }
      }
      if (wait === null) {
        wait = Math.max(0, seconds - move - this.elapsed);
        this.runStart = Date.now();
      }

      this.timer = setTimeout(function () {
        self.timer = null;
        self.goTo(self.index + 1);
      }, wait * 1000);

      // 10. Start the next slide's video WARM_SECONDS before its turn (at
      // once if the slide is shorter than that).
      this.warmTimer = setTimeout(function () {
        self.warmTimer = null;
        self.warmNext();
      }, Math.max(0, wait - WARM_SECONDS) * 1000);
    }

    // Seconds slide i stays on screen: its own setting, at least 3s.
    slideSeconds(i) {
      var slide = this.slides[i];
      var seconds = slide ? Number(slide.getAttribute('data-lsc-duration')) : 0;
      return Math.max(3, seconds || this.defaultSeconds);
    }

    // Button shows "play" while held by the visitor or by reduced motion.
    // Its name says what it controls: the slideshow when auto-rotation is
    // on, otherwise just the video.
    // LSC - Oct 11, 2026 (audit fix A1): the words come from the button's
    // data-lsc-label-play / data-lsc-label-pause, which the section fills
    // from the theme's translations (slideshow or video wording). English
    // is only a fallback for markup without them.
    updatePauseButton() {
      var button = this.pauseButton;
      if (!button) return;
      var paused = !!(this.pauseReasons.user || this.pauseReasons.motion);
      var what = this.autoplayEnabled ? 'slideshow' : 'video';
      var label = paused
        ? button.getAttribute('data-lsc-label-play') || 'Play ' + what
        : button.getAttribute('data-lsc-label-pause') || 'Pause ' + what;
      button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      button.setAttribute('aria-label', label);
    }
  }

  window.customElements.define('lsc-hero-slideshow', LscHeroSlideshow);
})();
